import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Admin user type
 */
export interface AdminUser {
  id: string;
  email: string;
  role: 'admin' | 'super_admin';
  lastLoginAt: string | null;
  totpEnabled?: boolean;
}

/**
 * Auth store state interface
 */
interface AuthState {
  // State
  admin: AdminUser | null;
  csrfToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  mustChangePassword: boolean;

  // Actions
  setAdmin: (admin: AdminUser | null) => void;
  setCsrfToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setMustChangePassword: (mustChange: boolean) => void;
  login: (admin: AdminUser, csrfToken: string, mustChangePassword?: boolean) => void;
  logout: () => void;
  clearError: () => void;
}

/**
 * Auth store for admin authentication state.
 * Persists authentication state to sessionStorage.
 * 
 * Requirements: 1.1, 1.5, 1.6
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      admin: null,
      csrfToken: null,
      isAuthenticated: false,
      isLoading: true, // Start loading to check auth on mount
      error: null,
      mustChangePassword: false,

      // Actions
      setAdmin: (admin) =>
        set({
          admin,
          isAuthenticated: admin !== null,
        }),

      setCsrfToken: (csrfToken) => set({ csrfToken }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      setMustChangePassword: (mustChangePassword) => set({ mustChangePassword }),

      login: (admin, csrfToken, mustChangePassword = false) =>
        set({
          admin,
          csrfToken,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          mustChangePassword,
        }),

      logout: () =>
        set({
          admin: null,
          csrfToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          mustChangePassword: false,
        }),

      clearError: () => set({ error: null }),
    }),
    {
      name: 'admin-auth',
      // Only persist essential auth data, not loading/error states
      partialize: (state) => ({
        admin: state.admin,
        csrfToken: state.csrfToken,
        isAuthenticated: state.isAuthenticated,
        mustChangePassword: state.mustChangePassword,
      }),
    }
  )
);

/**
 * Helper to check if current admin is a super admin
 */
export function isSuperAdmin(admin: AdminUser | null): boolean {
  return admin?.role === 'super_admin';
}
